import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Activity, Bot, Check, Copy, KeyRound, Pencil, Play, Plus, RotateCcw, ShieldOff, Undo2 } from 'lucide-react';
import { Botao, Cartao, Indicador, Selo } from '../componentes/base';
import { Carregando, Erro, Estado, Vazio } from '../componentes/estados';
import { Alternador, CampoData, Entrada, EntradaSensivel, Rotulo, Selecao } from '../componentes/formulario';
import { SeletorPeriodo } from '../componentes/periodo/SeletorPeriodo';
import { escolhaDoPeriodo, hojeNoFuso, periodoDaEscolha, periodoDoAtalho, type Periodo, type PeriodoGuardado } from '../componentes/periodo/periodo';
import { Confirmar, Drawer, Modal } from '../componentes/sobreposicao';
import { rotuloDoItem } from '../componentes/lixeira';
import { TextoCortado } from '../componentes/texto-cortado';
import { useToast } from '../componentes/toast';
import { useValidacaoLocal } from '../componentes/validacao-local';
import { ErroRequisicao, get, mensagemDeErro, patch, post } from '../nucleo/api';
import { COR_NIVEL, type Aprovacao, type Assistente, type ItemLixeira, type OpcoesAssistente, type UsoAssistente } from '../nucleo/assistentes/dados';
import { data, dataHora, deCentavos, dinheiro, numero, paraCentavos } from '../nucleo/formato';
import { rotuloAcao, rotuloEntidade, rotuloModulo } from '../nucleo/rotulos';
import { useSessao } from '../nucleo/sessao';

/**
 * Fase 2 (W11): Minha conta > Meus assistentes. Cada pessoa cria o próprio assistente (agente de IA) com escopo e limites,
 * vê o que ele fez, revoga, aprova o que ele preparou, acompanha o uso e desfaz o que ele excluiu. O assistente nunca passa
 * das permissões da pessoa; o que é sensível espera a confirmação dela, e o que é reservado ele nunca faz.
 */

const CHAVE_ASSISTENTES = ['assistentes'];
const COR_ESTADO = { ativo: 'verde', suspenso: 'laranja', revogado: 'neutro' } as const;
const DIAS = [1, 2, 3, 4, 5, 6, 0] as const;
const PRAZOS = [1, 4, 8, 24, 48, 72, 168] as const;

async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------------------------------------- chave nova
function ModalChave({ chave, aoFechar }: { chave: string; aoFechar: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('assistentes.chave.titulo')}
      rodape={
        <>
          <Botao
            icone={copiado ? <Check className="size-4 text-verde" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            onClick={async () => {
              if (await copiar(chave)) {
                setCopiado(true);
                toast.sucesso(t('comum.copiado'));
              } else toast.erro(t('comum.copiar'));
            }}
          >
            {t('assistentes.chave.copiar')}
          </Botao>
          <Botao variante="primario" onClick={aoFechar}>
            {t('assistentes.chave.guardei')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-chave-assistente>
        <p className="text-sm text-tinta-2">{t('assistentes.chave.texto')}</p>
        <code className="numero rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm break-all text-tinta-1">{chave}</code>
        <p className="text-sm text-tinta-3">{t('assistentes.chave.como', { url: `${window.location.origin}/mcp` })}</p>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------------------- formulário
export interface Valores {
  nome: string;
  todos: boolean;
  modulos: Record<string, '' | 'leitura' | 'escrita'>;
  confirmarTudo: boolean;
  acoes_por_dia: string;
  valor_maximo: string;
  horario_inicio: string;
  horario_fim: string;
  dias_semana: number[];
  prazo_aprovacao_horas: number;
  anomalia_exclusoes: string;
  anomalia_janela_min: string;
}

export function valoresIniciais(a: Assistente | null): Valores {
  const escopos = a?.escopos ?? ['*'];
  const modulos: Valores['modulos'] = {};
  for (const e of escopos) {
    const [m, tipo] = e.split(':');
    if (m && m !== '*' && (tipo === 'leitura' || tipo === 'escrita')) modulos[m] = tipo;
  }
  return {
    nome: a?.nome ?? '',
    todos: escopos.includes('*'),
    modulos,
    confirmarTudo: a?.niveis?.['*'] === 2,
    acoes_por_dia: a?.acoes_por_dia ? String(a.acoes_por_dia) : '',
    valor_maximo: a?.valor_maximo_centavos !== null && a?.valor_maximo_centavos !== undefined ? deCentavos(Number(a.valor_maximo_centavos)) : '',
    horario_inicio: a?.horario_inicio?.slice(0, 5) ?? '',
    horario_fim: a?.horario_fim?.slice(0, 5) ?? '',
    dias_semana: a?.dias_semana ?? [],
    prazo_aprovacao_horas: a?.prazo_aprovacao_horas ?? 24,
    anomalia_exclusoes: String(a?.anomalia_exclusoes ?? 50),
    anomalia_janela_min: String(a?.anomalia_janela_min ?? 5),
  };
}

function NiveisDoDono({ opcoes, confirmarTudo }: { opcoes: OpcoesAssistente; confirmarTudo: boolean }) {
  const { t } = useTranslation();
  const grupos = [1, 2, 3].map((n) => ({
    n,
    itens: opcoes.permissoes.filter((p) => (confirmarTudo && p.nivel_papel === 1 ? 2 : p.nivel_papel) === n),
  }));
  return (
    <details className="rounded-lg border border-borda">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-tinta-1">{t('assistentes.form.niveisTitulo')}</summary>
      <div className="flex flex-col gap-3 border-t border-borda px-3 py-3" data-niveis-dono>
        <p className="text-xs text-tinta-3">{t('assistentes.form.niveisTexto')}</p>
        {grupos.map((g) => (
          <div key={g.n} className="flex flex-col gap-1.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-tinta-2">
              <Selo cor={COR_NIVEL[g.n]}>N{g.n}</Selo>
              {t(`assistentes.niveis.n${g.n}`)}
            </p>
            {g.itens.length ? (
              <ul className="flex flex-wrap gap-1.5">
                {g.itens.map((p) => (
                  <li key={p.codigo}>
                    <Selo quebra>{`${rotuloModulo(p.modulo)}: ${rotuloAcao(p.codigo.slice(p.codigo.indexOf('.') + 1))}`}</Selo>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-tinta-3">{t('assistentes.form.nenhumaAcao')}</p>
            )}
          </div>
        ))}
      </div>
    </details>
  );
}

function FormAssistente({ atual, aoFechar, aoCriado }: { atual: Assistente | null; aoFechar: () => void; aoCriado: (chave: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const opcoes = useQuery({ queryKey: ['assistentes', 'opcoes'], queryFn: () => get<OpcoesAssistente>('/assistentes/opcoes') });
  const [v, setV] = useState<Valores>(() => valoresIniciais(atual));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [geral, setGeral] = useState<string | null>(null);
  // Data e hora inválidas no formulário barram o envio e levam o foco ao campo (r5 robustez M4).
  const { ref } = useValidacaoLocal(erros, geral);
  const muda = <K extends keyof Valores>(k: K, x: Valores[K]) => setV((o) => ({ ...o, [k]: x }));
  const salvar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => (atual ? patch(`/assistentes/${atual.id}`, corpo) : post<{ id: string; chave: string }>('/assistentes', corpo)),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: CHAVE_ASSISTENTES });
      if (!atual && r && typeof r === 'object' && 'chave' in r) aoCriado((r as { chave: string }).chave);
      else toast.sucesso(t('comum.salvo'));
      aoFechar();
    },
    onError: (e) => {
      const campo = e instanceof ErroRequisicao ? (e.metaDoErro()?.campo as string | undefined) : undefined;
      if (campo && ['nome', 'escopos', 'horario_fim'].includes(campo)) setErros({ [campo]: mensagemDeErro(e) });
      else setGeral(mensagemDeErro(e));
    },
  });
  function enviar() {
    setGeral(null);
    const novos: Record<string, string> = {};
    if (!v.nome.trim()) novos.nome = t('erros.obrigatorio');
    const escopos = v.todos ? ['*'] : Object.entries(v.modulos).flatMap(([m, x]) => (x ? [`${m}:${x}`] : []));
    if (!escopos.length) novos.escopos = t('assistentes.form.escolhaModulo');
    if (!!v.horario_inicio !== !!v.horario_fim) novos.horario_fim = t('assistentes.form.horarioIncompleto');
    const valor = v.valor_maximo.trim() ? paraCentavos(v.valor_maximo) : null;
    if (v.valor_maximo.trim() && (valor === null || valor < 0)) novos.valor_maximo = t('assistentes.form.valorInvalido');
    setErros(novos);
    if (Object.keys(novos).length) return;
    const inteiro = (s: string) => (s.trim() ? Math.max(1, Math.floor(Number(s))) : null);
    const niveis = { ...(atual?.niveis ?? {}) };
    if (v.confirmarTudo) niveis['*'] = 2;
    else delete niveis['*'];
    salvar.mutate({
      nome: v.nome.trim(),
      escopos,
      niveis,
      acoes_por_dia: inteiro(v.acoes_por_dia),
      valor_maximo_centavos: valor,
      horario_inicio: v.horario_inicio || null,
      horario_fim: v.horario_fim || null,
      dias_semana: v.dias_semana.length ? v.dias_semana : null,
      prazo_aprovacao_horas: v.prazo_aprovacao_horas,
      anomalia_exclusoes: inteiro(v.anomalia_exclusoes) ?? 50,
      anomalia_janela_min: inteiro(v.anomalia_janela_min) ?? 5,
    });
  }
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={atual ? t('assistentes.form.editar', { nome: atual.nome }) : t('assistentes.novo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {t('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} onClick={enviar}>
            {atual ? t('comum.salvar') : t('assistentes.form.criar')}
          </Botao>
        </>
      }
    >
      <Estado consulta={opcoes}>
        {(op) => (
          <form
            ref={ref}
            className="flex flex-col gap-5"
            noValidate
            data-form-assistente
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
          >
            {geral && (
              <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                {geral}
              </p>
            )}
            <Rotulo id="assistente-nome" rotulo={t('assistentes.form.nome')} erro={erros.nome} ajuda={t('assistentes.form.nomeAjuda')}>
              <Entrada id="assistente-nome" maxLength={60} value={v.nome} aria-invalid={!!erros.nome || undefined} aria-describedby={erros.nome ? 'assistente-nome-erro' : undefined} onChange={(e) => muda('nome', e.target.value)} />
            </Rotulo>

            <fieldset className="flex flex-col gap-3">
              <legend className="kicker mb-1">{t('assistentes.form.escopo')}</legend>
              <p className="text-sm text-tinta-3">{t('assistentes.form.escopoTexto')}</p>
              <label className="flex items-center justify-between gap-3 text-sm text-tinta-1">
                <span id="rotulo-todos">{t('assistentes.form.todos')}</span>
                <Alternador marcado={v.todos} aoMudar={(x) => muda('todos', x)} rotulo={t('assistentes.form.todos')} />
              </label>
              {!v.todos && (
                <div className="grid gap-3 sm:grid-cols-2" data-escopo-modulos>
                  {op.modulos.map((m) => (
                    <Rotulo key={m.slug} id={`escopo-${m.slug}`} rotulo={rotuloModulo(m.slug, m.nome)}>
                      <Selecao
                        id={`escopo-${m.slug}`}
                        value={v.modulos[m.slug] ?? ''}
                        onChange={(e) => muda('modulos', { ...v.modulos, [m.slug]: e.target.value as Valores['modulos'][string] })}
                        opcoes={[
                          { valor: '', rotulo: t('assistentes.form.semAcesso') },
                          { valor: 'leitura', rotulo: t('assistentes.form.leitura') },
                          ...(m.reservado || !m.pode_escrever ? [] : [{ valor: 'escrita', rotulo: t('assistentes.form.escrita') }]),
                        ]}
                      />
                    </Rotulo>
                  ))}
                </div>
              )}
              {erros.escopos && (
                <p role="alert" className="text-sm text-perigo">
                  {erros.escopos}
                </p>
              )}
            </fieldset>

            <fieldset className="flex flex-col gap-3">
              <legend className="kicker mb-1">{t('assistentes.form.confirmacao')}</legend>
              <label className="flex items-center justify-between gap-3 text-sm text-tinta-1">
                <span>{t('assistentes.form.confirmarTudo')}</span>
                <Alternador marcado={v.confirmarTudo} aoMudar={(x) => muda('confirmarTudo', x)} rotulo={t('assistentes.form.confirmarTudo')} />
              </label>
              <Rotulo id="assistente-prazo" rotulo={t('assistentes.form.prazo')}>
                <Selecao
                  id="assistente-prazo"
                  value={String(v.prazo_aprovacao_horas)}
                  onChange={(e) => muda('prazo_aprovacao_horas', Number(e.target.value))}
                  opcoes={PRAZOS.map((h) => ({ valor: String(h), rotulo: t('assistentes.form.horas', { count: h }) }))}
                />
              </Rotulo>
              <NiveisDoDono opcoes={op} confirmarTudo={v.confirmarTudo} />
            </fieldset>

            <fieldset className="flex flex-col gap-3">
              <legend className="kicker mb-1">{t('assistentes.form.limites')}</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <Rotulo id="assistente-dia" rotulo={t('assistentes.form.acoesPorDia')} ajuda={t('assistentes.form.semLimite')}>
                  <Entrada id="assistente-dia" className="numero" inputMode="numeric" value={v.acoes_por_dia} onChange={(e) => muda('acoes_por_dia', e.target.value.replace(/\D/g, ''))} />
                </Rotulo>
                <Rotulo id="assistente-valor" rotulo={t('assistentes.form.valorMaximo')} erro={erros.valor_maximo} ajuda={t('assistentes.form.semLimite')}>
                  {/* Review r16 spec M3: o teto é dinheiro; no modo discreto, o marcador sem foco como todo campo de dinheiro. */}
                  <EntradaSensivel
                    mascara="dinheiro"
                    id="assistente-valor"
                    className="numero text-right"
                    inputMode="decimal"
                    value={v.valor_maximo}
                    aria-invalid={!!erros.valor_maximo || undefined}
                    aria-describedby={erros.valor_maximo ? 'assistente-valor-erro' : undefined}
                    onChange={(e) => muda('valor_maximo', e.target.value)}
                  />
                </Rotulo>
                <Rotulo id="assistente-inicio" rotulo={t('assistentes.form.horarioInicio')}>
                  <CampoData id="assistente-inicio" type="time" value={v.horario_inicio} onChange={(e) => muda('horario_inicio', e.target.value)} />
                </Rotulo>
                <Rotulo id="assistente-fim" rotulo={t('assistentes.form.horarioFim')} erro={erros.horario_fim}>
                  <CampoData id="assistente-fim" type="time" value={v.horario_fim} onChange={(e) => muda('horario_fim', e.target.value)} />
                </Rotulo>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-tinta-2">{t('assistentes.form.dias')}</span>
                {/* Review r16 visual B3: escolha de vários dias no padrão das opções marcáveis do núcleo (caixa e rótulo, como os
                    lembretes da Agenda), não em botões feitos à mão. */}
                <div className="flex flex-wrap gap-2" role="group" aria-label={t('assistentes.form.dias')}>
                  {DIAS.map((d) => {
                    const marcado = v.dias_semana.includes(d);
                    return (
                      <label key={d} data-dia-assistente={d} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${marcado ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:bg-sup-2'}`}>
                        <input type="checkbox" checked={marcado} onChange={(e) => muda('dias_semana', e.target.checked ? [...v.dias_semana, d] : v.dias_semana.filter((x) => x !== d))} />
                        {t(`assistentes.dias.${d}`)}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-tinta-3">{t('assistentes.form.diasAjuda')}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Rotulo id="assistente-anomalia" rotulo={t('assistentes.form.anomaliaExclusoes')}>
                  <Entrada id="assistente-anomalia" className="numero" inputMode="numeric" value={v.anomalia_exclusoes} onChange={(e) => muda('anomalia_exclusoes', e.target.value.replace(/\D/g, ''))} />
                </Rotulo>
                <Rotulo id="assistente-janela" rotulo={t('assistentes.form.anomaliaJanela')}>
                  <Entrada id="assistente-janela" className="numero" inputMode="numeric" value={v.anomalia_janela_min} onChange={(e) => muda('anomalia_janela_min', e.target.value.replace(/\D/g, ''))} />
                </Rotulo>
              </div>
              <p className="text-xs text-tinta-3">{t('assistentes.form.anomaliaAjuda')}</p>
            </fieldset>
          </form>
        )}
      </Estado>
    </Drawer>
  );
}

// ------------------------------------------------------------------------------------------------------------ atividade
function AtividadeAssistente({ id }: { id: string }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['assistentes', id, 'atividade'], queryFn: () => get<{ itens: { id: string; acao: string; entidade: string; criado_em: string; aprovacao_id: string | null }[] }>(`/assistentes/${id}/atividade`) });
  return (
    <Estado consulta={q}>
      {(d) =>
        d.itens.length === 0 ? (
          <p className="text-sm text-tinta-3">{t('assistentes.atividade.vazio')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda" data-atividade>
            {d.itens.map((i) => (
              <li key={i.id} className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <span className="min-w-0 text-tinta-1">
                  {rotuloAcao(i.acao)} <span className="text-tinta-3">{rotuloEntidade(i.entidade)}</span>
                </span>
                <span className="flex items-center gap-2 text-xs text-tinta-3">
                  {i.aprovacao_id && <Selo cor="laranja">{t('assistentes.atividade.aprovada')}</Selo>}
                  {dataHora(i.criado_em)}
                </span>
              </li>
            ))}
          </ul>
        )
      }
    </Estado>
  );
}

// --------------------------------------------------------------------------------------------------------- um assistente
export function escopoLegivel(t: (k: string) => string, escopos: string[] | null): string[] {
  if (!escopos?.length) return [];
  if (escopos.includes('*')) return [t('assistentes.escopoTodos')];
  return escopos.map((e) => {
    const [m, tipo] = e.split(':');
    return `${rotuloModulo(m!)}: ${tipo === 'escrita' ? t('assistentes.form.escrita') : t('assistentes.form.leitura')}`;
  });
}

function CartaoAssistente({ a, aoEditar, aoChave }: { a: Assistente; aoEditar: () => void; aoChave: (c: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [verAtividade, setVerAtividade] = useState(false);
  const [confirmar, setConfirmar] = useState<'revogar' | 'chave' | null>(null);
  const invalidar = () => qc.invalidateQueries({ queryKey: CHAVE_ASSISTENTES });
  const acao = useMutation({
    mutationFn: async (tipo: 'revogar' | 'chave' | 'reativar') => (tipo === 'chave' ? post<{ chave: string }>(`/assistentes/${a.id}/chave`) : post(`/assistentes/${a.id}/${tipo}`)),
    onSuccess: async (r, tipo) => {
      setConfirmar(null);
      await invalidar();
      if (tipo === 'chave' && r && typeof r === 'object' && 'chave' in r) aoChave((r as { chave: string }).chave);
      else toast.sucesso(tipo === 'revogar' ? t('assistentes.revogado') : t('assistentes.reativado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const ativo = a.estado !== 'revogado';
  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-xl border border-borda bg-sup-1 p-4" data-assistente={a.estado}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Bot className="size-5 shrink-0 text-tinta-3" aria-hidden />
          <TextoCortado como="h3" className="font-semibold text-tinta-1">{a.nome}</TextoCortado>
        </div>
        <Selo cor={COR_ESTADO[a.estado]}>{t(`assistentes.estados.${a.estado}`)}</Selo>
      </div>
      {a.estado === 'suspenso' && (
        <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
          {a.suspenso_motivo === 'anomalia' ? t('assistentes.suspensoAnomalia') : t('assistentes.suspenso')}
        </p>
      )}
      <ul className="flex flex-wrap gap-1.5">
        {escopoLegivel(t, a.escopos).map((x) => (
          <li key={x}>
            <Selo quebra>{x}</Selo>
          </li>
        ))}
      </ul>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
        <div className="flex justify-between gap-2">
          <dt className="text-tinta-3">{t('assistentes.campos.chave')}</dt>
          <dd className="numero text-tinta-2">{a.prefixo ? `${a.prefixo}_...` : t('assistentes.semChave')}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-tinta-3">{t('assistentes.campos.ultimoUso')}</dt>
          <dd className="text-tinta-2">{a.ultimo_uso ? dataHora(a.ultimo_uso) : t('assistentes.nunca')}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-tinta-3">{t('assistentes.campos.hoje')}</dt>
          <dd className="numero text-tinta-2">{a.acoes_por_dia ? t('assistentes.campos.deLimite', { n: numero(a.acoes_hoje), limite: numero(a.acoes_por_dia) }) : numero(a.acoes_hoje)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-tinta-3">{t('assistentes.campos.pendentes')}</dt>
          <dd className="numero text-tinta-2">{numero(a.pendentes)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-tinta-3">{t('assistentes.campos.valorMaximo')}</dt>
          <dd className="numero text-tinta-2">{a.valor_maximo_centavos !== null ? dinheiro(Number(a.valor_maximo_centavos)) : t('assistentes.semLimiteCurto')}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-tinta-3">{t('assistentes.campos.horario')}</dt>
          <dd className="numero text-tinta-2">{a.horario_inicio && a.horario_fim ? t('comum.horario', { de: a.horario_inicio.slice(0, 5), ate: a.horario_fim.slice(0, 5) }) : t('assistentes.qualquerHora')}</dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-2 border-t border-borda pt-3">
        <Botao tamanho="sm" icone={<Activity className="size-4" aria-hidden />} aria-expanded={verAtividade} onClick={() => setVerAtividade((x) => !x)}>
          {t('assistentes.verAtividade')}
        </Botao>
        {ativo && (
          <Botao tamanho="sm" icone={<Pencil className="size-4" aria-hidden />} onClick={aoEditar}>
            {t('comum.editar')}
          </Botao>
        )}
        {ativo && (
          <Botao tamanho="sm" icone={<KeyRound className="size-4" aria-hidden />} onClick={() => setConfirmar('chave')}>
            {t('assistentes.novaChave')}
          </Botao>
        )}
        {a.estado === 'suspenso' && (
          <Botao tamanho="sm" variante="primario" icone={<Play className="size-4" aria-hidden />} carregando={acao.isPending && acao.variables === 'reativar'} onClick={() => acao.mutate('reativar')}>
            {t('assistentes.reativar')}
          </Botao>
        )}
        {ativo && (
          <Botao tamanho="sm" variante="perigo" icone={<ShieldOff className="size-4" aria-hidden />} onClick={() => setConfirmar('revogar')}>
            {t('assistentes.revogar')}
          </Botao>
        )}
      </div>
      {verAtividade && <AtividadeAssistente id={a.id} />}
      <Confirmar
        aberto={confirmar !== null}
        aoFechar={() => setConfirmar(null)}
        perigoso={confirmar === 'revogar'}
        carregando={acao.isPending}
        aoConfirmar={() => confirmar && acao.mutate(confirmar)}
        titulo={confirmar === 'revogar' ? t('assistentes.revogarTitulo', { nome: a.nome }) : t('assistentes.novaChaveTitulo')}
        texto={confirmar === 'revogar' ? t('assistentes.revogarTexto') : t('assistentes.novaChaveTexto')}
      />
    </li>
  );
}

// ------------------------------------------------------------------------------------------------------------ pendentes
function CartaoPendentes() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const q = useQuery({ queryKey: ['aprovacoes', 'lista', 'pendente'], queryFn: () => get<{ itens: Aprovacao[] }>('/aprovacoes', { estado: 'pendente' }) });
  if (q.isPending || q.isError || !q.data.itens.length) return null;
  return (
    <Cartao titulo={t('assistentes.pendentes.titulo', { count: q.data.itens.length })}>
      <ul className="flex flex-col divide-y divide-borda" data-pendentes>
        {q.data.itens.map((a) => (
          <li key={a.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <p className="text-sm text-tinta-1">
                {t('assistentes.pendentes.linha', { assistente: a.assistente_nome, acao: rotuloAcao(a.resumo.acao), modulo: rotuloModulo(a.modulo) })}
              </p>
              <p className="text-xs text-tinta-3">{t('assistentes.pendentes.expira', { data: dataHora(a.expira_em) })}</p>
            </div>
            <Botao tamanho="sm" variante="primario" onClick={() => navegar(`/aprovar/${a.id}`)}>
              {t('assistentes.pendentes.revisar')}
            </Botao>
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

// ------------------------------------------------------------------------------------------------------------ uso
function formatarTempo(t: (k: string, o?: Record<string, unknown>) => string, s: number | null): string {
  if (s === null) return t('assistentes.uso.semDado');
  if (s < 60) return t('assistentes.uso.segundos', { n: numero(s) });
  if (s < 3600) return t('assistentes.uso.minutos', { n: numero(Math.round(s / 60)) });
  return t('assistentes.uso.horas', { n: numero(s / 3600, 1) });
}

function PainelUso() {
  const { t } = useTranslation();
  const s = useSessao();
  const hoje = hojeNoFuso(s.workspace.fuso);
  const [periodo, setPeriodo] = useState<Periodo>(() => periodoDoAtalho('30_dias', hoje));
  const [pessoa, setPessoa] = useState(s.usuario.id);
  const escolha = escolhaDoPeriodo(periodo, hoje);
  const q = useQuery({
    queryKey: ['assistentes', 'uso', periodo.de, periodo.ate, pessoa],
    queryFn: () => get<UsoAssistente>('/assistentes/uso', { de: periodo.de, ate: periodo.ate, ...(pessoa !== s.usuario.id ? { usuario_id: pessoa } : {}) }),
    placeholderData: keepPreviousData,
  });
  const aoEscolher = (e: PeriodoGuardado) => setPeriodo(periodoDaEscolha(e, hoje));
  return (
    <Cartao titulo={t('assistentes.uso.titulo')}>
      <div className="flex flex-col gap-4" data-painel-uso aria-busy={q.isFetching}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="text-sm text-tinta-3">{t('assistentes.uso.texto', { de: data(periodo.de), ate: data(periodo.ate) })}</p>
          <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">
            {!!q.data?.pessoas.length && (
              <Selecao
                aria-label={t('assistentes.uso.pessoa')}
                value={pessoa}
                onChange={(e) => setPessoa(e.target.value)}
                opcoes={q.data.pessoas.map((p) => ({ valor: p.id, rotulo: p.id === s.usuario.id ? t('assistentes.uso.eu', { nome: p.nome }) : p.nome }))}
              />
            )}
            <SeletorPeriodo periodo={periodo} escolha={escolha} aoEscolher={aoEscolher} />
          </div>
        </div>
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : q.data.acionamentos === 0 && q.data.preparadas === 0 && q.data.feitas === 0 ? (
          <Vazio icone={<Bot className="size-5" aria-hidden />} titulo={t('assistentes.uso.vazioTitulo')} texto={t('assistentes.uso.vazioTexto')} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Indicador rotulo={t('assistentes.uso.acionamentos')} valor={numero(q.data.acionamentos)} />
              <Indicador rotulo={t('assistentes.uso.feitas')} valor={numero(q.data.feitas)} cor="verde" />
              <Indicador rotulo={t('assistentes.uso.preparadas')} valor={numero(q.data.preparadas)} cor="laranja" detalhe={t('assistentes.uso.pendentesAgora', { count: q.data.pendentes })} />
              <Indicador rotulo={t('assistentes.uso.tempoMedio')} valor={formatarTempo(t, q.data.tempo_medio_confirmacao_s)} />
              <Indicador rotulo={t('assistentes.uso.aprovadas')} valor={numero(q.data.aprovadas)} cor="verde" detalhe={q.data.falharam ? t('assistentes.uso.falharam', { count: q.data.falharam }) : undefined} />
              <Indicador rotulo={t('assistentes.uso.recusadas')} valor={numero(q.data.recusadas)} />
              <Indicador rotulo={t('assistentes.uso.expiradas')} valor={numero(q.data.expiradas)} />
              <Indicador
                rotulo={t('assistentes.uso.viaAssistente')}
                valor={q.data.alteracoes.percentual_assistente === null ? t('assistentes.uso.semDado') : `${numero(q.data.alteracoes.percentual_assistente, 1)}%`}
                cor="ciano"
                detalhe={t('assistentes.uso.viaDetalhe', { via: numero(q.data.alteracoes.via_assistente), direto: numero(q.data.alteracoes.direto) })}
              />
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="kicker">{t('assistentes.uso.modulos')}</h3>
              {q.data.modulos.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('assistentes.uso.semModulos')}</p>
              ) : (
                <ul className="flex flex-col gap-2" data-uso-modulos>
                  {q.data.modulos.map((m) => {
                    const max = q.data.modulos[0]!.acoes || 1;
                    return (
                      <li key={m.modulo} className="flex min-w-0 items-center gap-3 text-sm">
                        <span className="w-32 shrink-0 truncate text-tinta-2" title={rotuloModulo(m.modulo)}>
                          {rotuloModulo(m.modulo)}
                        </span>
                        <span className="h-2 min-w-0 flex-1 rounded-full bg-sup-3" aria-hidden>
                          <span className="block h-2 rounded-full bg-acento-forte" style={{ width: `${Math.max(4, Math.round((m.acoes / max) * 100))}%` }} />
                        </span>
                        <span className="numero w-12 shrink-0 text-right text-tinta-1">{numero(m.acoes)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Cartao>
  );
}

// ------------------------------------------------------------------------------------------------------------ lixeira
export function CartaoLixeira() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['assistentes', 'lixeira'], queryFn: () => get<{ itens: ItemLixeira[] }>('/lixeira', { meus_assistentes: 'true' }) });
  const restaurar = useMutation({
    mutationFn: (i: ItemLixeira) => post(`/lixeira/${i.entidade}/${i.id}/restaurar`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['assistentes', 'lixeira'] });
      toast.sucesso(t('assistentes.lixeira.restaurado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('assistentes.lixeira.titulo')}>
      <div className="flex flex-col gap-3" data-lixeira>
        <p className="text-sm text-tinta-3">{t('assistentes.lixeira.texto')}</p>
        <Estado consulta={q}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio icone={<Undo2 className="size-5" aria-hidden />} titulo={t('assistentes.lixeira.vazioTitulo')} texto={t('assistentes.lixeira.vazioTexto')} />
            ) : (
              <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
                {d.itens.map((i) => (
                  <li key={`${i.entidade}:${i.id}`} className="flex min-w-0 flex-wrap items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      {/* Review r16 spec M3 e B3: o nome pela mesma regra da lixeira do núcleo (iniciais do parceiro com a opção de
                          nomes, texto livre censurado) e o corte num bloco (o `span` em linha não corta e alargava a tela a 390). */}
                      <TextoCortado como="p" className="text-sm text-tinta-1">{i.rotulo ? rotuloDoItem(i) : rotuloEntidade(i.entidade)}</TextoCortado>
                      <p className="text-xs text-tinta-3">
                        {rotuloEntidade(i.entidade)} · {t('assistentes.lixeira.ate', { data: data(i.restaurar_ate) })}
                      </p>
                    </div>
                    {i.pode_restaurar && (
                      <Botao tamanho="sm" icone={<RotateCcw className="size-4" aria-hidden />} carregando={restaurar.isPending && restaurar.variables?.id === i.id} onClick={() => restaurar.mutate(i)}>
                        {t('assistentes.lixeira.desfazer')}
                      </Botao>
                    )}
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      </div>
    </Cartao>
  );
}

// ------------------------------------------------------------------------------------------------------------ aba
export function AbaAssistentes() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: CHAVE_ASSISTENTES, queryFn: () => get<{ itens: Assistente[] }>('/assistentes') });
  const [form, setForm] = useState<{ atual: Assistente | null } | null>(null);
  const [chave, setChave] = useState<string | null>(null);
  const ativos = useMemo(() => q.data?.itens.filter((a) => a.estado !== 'revogado').length ?? 0, [q.data]);
  return (
    <div className="flex flex-col gap-4" data-aba-assistentes>
      <CartaoPendentes />
      <Cartao
        titulo={t('assistentes.titulo')}
        acao={
          <Botao tamanho="sm" variante="primario" icone={<Plus className="size-4" aria-hidden />} disabled={ativos >= 10} onClick={() => setForm({ atual: null })}>
            {t('assistentes.novo')}
          </Botao>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-tinta-3">{t('assistentes.texto')}</p>
          <Estado consulta={q}>
            {(d) =>
              d.itens.length === 0 ? (
                <Vazio
                  icone={<Bot className="size-5" aria-hidden />}
                  titulo={t('assistentes.vazioTitulo')}
                  texto={t('assistentes.vazioTexto')}
                  acao={
                    // Review r16 visual M2: o primário da vista é o "Novo assistente" do cartão; a ação do Vazio é secundária.
                    <Botao icone={<Plus className="size-4" aria-hidden />} onClick={() => setForm({ atual: null })}>
                      {t('assistentes.novo')}
                    </Botao>
                  }
                />
              ) : (
                <ul className="grid gap-3 lg:grid-cols-2" data-lista-assistentes>
                  {d.itens.map((a) => (
                    <CartaoAssistente key={a.id} a={a} aoEditar={() => setForm({ atual: a })} aoChave={setChave} />
                  ))}
                </ul>
              )
            }
          </Estado>
        </div>
      </Cartao>
      <PainelUso />
      <CartaoLixeira />
      {form && <FormAssistente atual={form.atual} aoFechar={() => setForm(null)} aoCriado={setChave} />}
      {chave && <ModalChave chave={chave} aoFechar={() => setChave(null)} />}
    </div>
  );
}
